import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Clock, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";

interface SearchAutocompleteProps {
  className?: string;
  onSearch?: () => void;
}

const SEARCH_HISTORY_KEY = "blog_search_history";
const MAX_HISTORY_ITEMS = 5;

export const SearchAutocomplete = ({ className, onSearch }: SearchAutocompleteProps) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [searchHistory, setSearchHistory] = useState<string[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Load search history from localStorage
    const history = localStorage.getItem(SEARCH_HISTORY_KEY);
    if (history) {
      setSearchHistory(JSON.parse(history));
    }
  }, []);

  useEffect(() => {
    // Close dropdown when clicking outside
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const fetchSuggestions = async () => {
      if (searchQuery.length < 2) {
        setSuggestions([]);
        return;
      }

      setIsLoading(true);
      try {
        const { data, error } = await supabase
          .from("blog_posts")
          .select("id, title, slug, excerpt")
          .eq("status", "published")
          .or(`title.ilike.%${searchQuery}%,excerpt.ilike.%${searchQuery}%`)
          .limit(5);

        if (error) throw error;
        setSuggestions(data || []);
      } catch (error) {
        console.error("Error fetching suggestions:", error);
        setSuggestions([]);
      } finally {
        setIsLoading(false);
      }
    };

    const debounceTimer = setTimeout(fetchSuggestions, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchQuery]);

  const saveToHistory = (query: string) => {
    const trimmedQuery = query.trim();
    if (!trimmedQuery) return;

    const updatedHistory = [
      trimmedQuery,
      ...searchHistory.filter((q) => q !== trimmedQuery),
    ].slice(0, MAX_HISTORY_ITEMS);

    setSearchHistory(updatedHistory);
    localStorage.setItem(SEARCH_HISTORY_KEY, JSON.stringify(updatedHistory));
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      saveToHistory(searchQuery);
      navigate(`/?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery("");
      setShowDropdown(false);
      onSearch?.();
    }
  };

  const handleSuggestionClick = (slug: string) => {
    navigate(`/blog/${slug}`);
    setShowDropdown(false);
    setSearchQuery("");
    onSearch?.();
  };

  const handleHistoryClick = (query: string) => {
    navigate(`/?search=${encodeURIComponent(query)}`);
    setShowDropdown(false);
    setSearchQuery("");
    onSearch?.();
  };

  const clearHistory = () => {
    setSearchHistory([]);
    localStorage.removeItem(SEARCH_HISTORY_KEY);
  };

  const removeHistoryItem = (query: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const updatedHistory = searchHistory.filter((q) => q !== query);
    setSearchHistory(updatedHistory);
    localStorage.setItem(SEARCH_HISTORY_KEY, JSON.stringify(updatedHistory));
  };

  return (
    <div className={cn("relative", className)}>
      <form onSubmit={handleSearch} className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground z-10" />
        <Input
          ref={inputRef}
          type="search"
          placeholder="Search articles..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onFocus={() => setShowDropdown(true)}
          className="w-full pl-9"
        />
      </form>

      {showDropdown && (searchQuery.length >= 2 || searchHistory.length > 0) && (
        <div
          ref={dropdownRef}
          className="absolute top-full mt-2 w-full bg-popover border border-border rounded-md shadow-lg z-50 max-h-[400px] overflow-y-auto"
        >
          {/* Search History */}
          {searchQuery.length < 2 && searchHistory.length > 0 && (
            <div className="p-2">
              <div className="flex items-center justify-between px-2 py-1">
                <span className="text-xs font-medium text-muted-foreground">Recent Searches</span>
                <button
                  onClick={clearHistory}
                  className="text-xs text-muted-foreground hover:text-foreground"
                >
                  Clear
                </button>
              </div>
              {searchHistory.map((query, index) => (
                <button
                  key={index}
                  onClick={() => handleHistoryClick(query)}
                  className="w-full flex items-center gap-2 px-2 py-2 hover:bg-accent rounded-md group"
                >
                  <Clock className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <span className="text-sm text-foreground flex-1 text-left truncate">{query}</span>
                  <button
                    onClick={(e) => removeHistoryItem(query, e)}
                    className="opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X className="h-3 w-3 text-muted-foreground hover:text-foreground" />
                  </button>
                </button>
              ))}
            </div>
          )}

          {/* Suggestions */}
          {searchQuery.length >= 2 && (
            <div className="p-2">
              {isLoading ? (
                <div className="px-2 py-3 text-sm text-muted-foreground">Loading...</div>
              ) : suggestions.length > 0 ? (
                <>
                  <div className="px-2 py-1 text-xs font-medium text-muted-foreground">
                    Articles
                  </div>
                  {suggestions.map((post) => (
                    <button
                      key={post.id}
                      onClick={() => handleSuggestionClick(post.slug)}
                      className="w-full text-left px-2 py-2 hover:bg-accent rounded-md"
                    >
                      <div className="text-sm font-medium text-foreground line-clamp-1">
                        {post.title}
                      </div>
                      {post.excerpt && (
                        <div className="text-xs text-muted-foreground line-clamp-1 mt-0.5">
                          {post.excerpt}
                        </div>
                      )}
                    </button>
                  ))}
                </>
              ) : (
                <div className="px-2 py-3 text-sm text-muted-foreground">
                  No articles found for "{searchQuery}"
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
